// W6: Health as five questions, in the order you would ask them. The phone's
// `HealthStrip` keeps the account sheet; this is the same facts read for a
// monitor. Shell draws the h1, so this draws none.

import { useMemo, useRef, useState } from 'react'
import { Button, Tag, type TagTone } from '../../ui'
import { toast } from '../../ui/Toast'
import {
  BROKEN_IS_FAILED, CARD, RECIPE_STATUS, STATUS, alertKey, alertLabel, alertTone, alertWords, dailySpend,
  recordedSpend, recordedSpendNote, statusSentence, withinDays,
} from '../../lib/healthWords'
import { useAcknowledgeAlert, useAddNote, useEditNote, useSetNoteDone } from '../../lib/mutations'
import { useAlerts, useHarvestRuns, useNotes, useRecipes, useRecordedSpend } from '../../lib/queries'
import { attempt, useWriteError } from '../../lib/useAction'
import { Failed } from '../../screens/Stub'
import { InviteForm } from '../../components/HealthStrip'
import { useIsAdmin, adminGate } from '../../lib/useIsAdmin'
import { useAuth } from '../../lib/auth'
import { isOwnNote, noteAuthor } from '../../lib/noteAuthor'
import type { NoteKind } from '../../lib/db'
import type { AppNote } from '../../lib/types'
import '../health.css'

const SPEND_DAYS = 14
const short = (d: Date) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
const RUN_TONE: Record<string, TagTone> = { repaired: 'warn', blocked: 'neutral', failed: 'bad' }
const RECIPE_TONE: Record<string, TagTone> = { ok: 'ok', paused: 'neutral', degraded: 'warn', broken: 'bad', blocked: 'bad' }
const RANK = ['broken', 'blocked', 'degraded', 'paused', 'ok']

/** U0.3: the shell already sends a non-admin elsewhere (`deskAccess`); this is
 *  the second no. A wrapper, so the hooks in the body run unconditionally. */
export function Health() {
  if (adminGate(useIsAdmin()) === 'hide') return <></>
  return <HealthBody />
}

function HealthBody() {
  const recipesQ = useRecipes()
  const alertsQ = useAlerts()
  const runsQ = useHarvestRuns(14)
  const notesQ = useNotes()
  // a3-31: the recorded total, from the rows — not a literal. Left out of
  // `readError` on purpose: a failure here drops one clause, not the screen.
  const spentQ = useRecordedSpend()
  const recorded = useMemo(() => (spentQ.data ? recordedSpend(spentQ.data) : null), [spentQ.data])
  const ack = useAcknowledgeAlert()
  const ackError = useWriteError(ack)

  // The probe rigs are paused on purpose and are not adapters; every count
  // leaves them out, as the rail's dot does.
  const recipes = useMemo(() => (recipesQ.data ?? []).filter(r => !r.id.startsWith('probe-')), [recipesQ.data])
  const alerts = alertsQ.data ?? []
  const runs = runsQ.data ?? []
  // The query carries 14 days for the cost card; everything called "this
  // week" is narrowed to seven by date.
  const weekRuns = useMemo(() => withinDays(runs, 7, Date.now()), [runs])
  const openNotes = (notesQ.data ?? []).filter(n => !n.done_at)

  const byStatus = useMemo(() => {
    const m = new Map<string, number>()
    for (const r of recipes) m.set(r.status, (m.get(r.status) ?? 0) + 1)
    return [...m.entries()].sort((a, b) => RANK.indexOf(a[0]) - RANK.indexOf(b[0]))
  }, [recipes])
  const notOkRecipes = recipes.filter(r => r.status !== 'ok')
    .sort((a, b) => RANK.indexOf(a.status) - RANK.indexOf(b.status) || a.name.localeCompare(b.name))

  const badRuns = useMemo(() => weekRuns.filter(r => r.status && r.status !== 'ok'), [weekRuns])

  // Fourteen bars, one per day; a day with no run is a zero bar. The status
  // sentence takes the week's spend, the card the fortnight's.
  const spend = useMemo(() => dailySpend(runs, SPEND_DAYS, new Date()), [runs])
  const usd = spend.reduce((a, s) => a + s.usd, 0)
  const weekUsd = useMemo(() => weekRuns.reduce((a, r) => a + (Number(r.model_cost_usd) || 0), 0), [weekRuns])
  const max = Math.max(0.01, ...spend.map(s => s.usd))

  const loading = recipesQ.isPending || alertsQ.isPending || runsQ.isPending
  // A read that failed with nothing cached: every card below would print
  // zeros and "Nothing." as if that were the harvest (a4-07). A failed
  // refetch over cached rows keeps the rows.
  const readError = [recipesQ, alertsQ, runsQ, notesQ].find(q => q.error && !q.data)?.error

  if (readError) return <div className="health"><Failed error={readError.message} /></div>

  return (
    <div className="health">
      <p className="status">
        {loading ? 'Reading the harvest…' : statusSentence({
          ok: recipes.filter(r => r.status === 'ok').length, total: recipes.length,
          alerts: alerts.length, badRuns: badRuns.length, spendUsd: weekUsd,
        }, recorded)}
      </p>

      <div className="health__grid">
        <section className="hcard" aria-labelledby="h-broken">
          <h2 id="h-broken">Is anything broken?<span className="n">{alerts.length}</span></h2>
          <p className="why">{CARD.broken}</p>
          <div className="hkey">
            {alertKey(alerts.map(a => a.kind)).map(k => (
              <span key={k}><Tag tone={alertTone(k)}>{alertLabel(k)}</Tag> {alertWords(k)}</span>
            ))}
          </div>
          <p className="why">{BROKEN_IS_FAILED}</p>
          {ackError && <p className="herr" role="alert">{ackError}</p>}
          {alerts.map(a => (
            <div className="hrow" key={a.id}>
              <span>
                <Tag tone={alertTone(a.kind)}>{alertLabel(a.kind)}</Tag>
                <span className="id">{a.recipe_id ?? 'the Worker'}</span>
              </span>
              <span className="w">{short(new Date(a.created_at))}</span>
              <span className="e">{a.message}</span>
              <span className="act">
                <Button size="sm" variant="secondary" disabled={ack.isPending && ack.variables === a.id}
                  onClick={() => ack.mutate(a.id)}>Acknowledge</Button>
              </span>
            </div>
          ))}
          {!alerts.length && <div className="none">Nothing. Every recipe that ran is fine or already acknowledged.</div>}
        </section>

        <section className="hcard" aria-labelledby="h-cost">
          <h2 id="h-cost">What did it cost?<span className="n">${usd.toFixed(2)}</span></h2>
          <p className="why">{CARD.cost}</p>
          <div className="hspend" role="img" aria-label={`Model spend, last ${SPEND_DAYS} days: $${usd.toFixed(2)}`}>
            {spend.map(s => (
              <i key={s.key} className={s.usd > 0 ? 'has' : ''} style={{ height: `${Math.max(3, s.usd / max * 100)}%` }}
                title={`${short(s.d)}: $${s.usd.toFixed(2)} over ${s.runs} runs`} />
            ))}
          </div>
          <div className="hspend__x"><span>{short(spend[0].d)}</span><span>spend, last {SPEND_DAYS} days</span><span>{short(spend[SPEND_DAYS - 1].d)}</span></div>
          {recordedSpendNote(recorded) && <div className="none" style={{ marginTop: 8 }}>{recordedSpendNote(recorded)}</div>}
        </section>

        <section className="hcard hcard--wide" aria-labelledby="h-week">
          <h2 id="h-week">What went wrong this week?<span className="n">{badRuns.length}</span></h2>
          <p className="why">{CARD.week}</p>
          <div className="hkey">
            {(Object.keys(STATUS) as (keyof typeof STATUS)[]).map(k => (
              <span key={k}><Tag tone={RUN_TONE[k]}>{k}</Tag> {STATUS[k]}</span>
            ))}
          </div>
          {badRuns.slice(0, 12).map((r, i) => {
            const t = new Date(r.started_at)
            return (
              <div className="hrow" key={`${r.recipe_id}-${r.started_at}-${i}`}>
                <span><Tag tone={RUN_TONE[r.status ?? ''] ?? 'neutral'}>{r.status}</Tag><span className="id">{r.recipe_id ?? 'the Worker'}</span></span>
                <span className="w">
                  {t.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}{' '}
                  {t.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                  {Number(r.model_cost_usd) ? ` · $${Number(r.model_cost_usd).toFixed(2)}` : ''}
                </span>
              </div>
            )
          })}
          {badRuns.length > 12 && <div className="none">and {badRuns.length - 12} older.</div>}
          {!badRuns.length && <div className="none">Every run this week came back clean.</div>}
        </section>

        <section className="hcard" aria-labelledby="h-recipes">
          <h2 id="h-recipes">The recipes<span className="n">{recipes.length}</span></h2>
          <p className="why">{CARD.recipes}</p>
          <div className="hkey">
            {(Object.keys(RECIPE_STATUS) as (keyof typeof RECIPE_STATUS)[]).map(k => (
              <span key={k}><Tag tone={RECIPE_TONE[k]}>{k}</Tag> {RECIPE_STATUS[k]}</span>
            ))}
          </div>
          {byStatus.map(([s, n]) => (
            <div className="hrow" key={s}>
              <span><Tag tone={RECIPE_TONE[s] ?? 'neutral'}>{s}</Tag></span>
              <span className="w">{n}</span>
            </div>
          ))}
          {notOkRecipes.length > 0 && <h3 className="hsub">Not ok, and when each last was</h3>}
          {notOkRecipes.map(r => (
            <div className="hrow" key={r.id}>
              <span><Tag tone={RECIPE_TONE[r.status] ?? 'neutral'}>{r.status}</Tag><span className="id">{r.name}</span></span>
              <span className="w">{r.last_ok_at ? `last ok ${short(new Date(r.last_ok_at))}` : 'never ok'}</span>
            </div>
          ))}
          <div className="hadd"><div style={{ flex: 1 }}><InviteForm /></div></div>
        </section>

        <NotesCard notes={openNotes} />
      </div>
    </div>
  )
}

/** One compact line per open note; the resolution field and the actions open
 *  only on the selected one. "Closed." stays in the card after a close, because
 *  the shared Toast is one slot and reads as a stale error over the next note. */
function NotesCard({ notes }: { notes: AppNote[] }) {
  const [sel, setSel] = useState<string | null>(null)
  const [closed, setClosed] = useState(false)
  // The writes and the resolution draft live here, not on the row: closing
  // patches the note out of `notes` at once, so the row unmounts before the
  // write lands, and a rollback mounts a fresh one. Held on the row, the
  // error and the sentence typed would both go with it.
  const done = useSetNoteDone()
  const edit = useEditNote()
  const add = useAddNote()
  const error = useWriteError(done, edit, add)
  const [res, setRes] = useState('')
  return (
    <section className="hcard" aria-labelledby="h-notes">
      <h2 id="h-notes">Notes<span className="n">{notes.length}</span></h2>
      <p className="why">{CARD.notes}</p>
      {closed && <div className="hclosed" role="status">Closed.</div>}
      {error && <p className="herr" role="alert">{error}</p>}
      {notes.map(n => (
        <NoteRow key={n.id} note={n} open={sel === n.id} done={done} edit={edit} res={res} setRes={setRes}
          onToggle={() => { setClosed(false); setRes(''); setSel(sel === n.id ? null : n.id) }}
          onClosed={() => { setSel(null); setRes(''); setClosed(true) }} />
      ))}
      {!notes.length && <div className="none">None open.</div>}
      <AddNote add={add} />
    </section>
  )
}

function NoteRow({ note, open, done, edit, res, setRes, onToggle, onClosed }: {
  note: AppNote; open: boolean
  done: ReturnType<typeof useSetNoteDone>; edit: ReturnType<typeof useEditNote>
  res: string; setRes: (v: string) => void
  onToggle: () => void; onClosed: () => void
}) {
  const uid = useAuth().session?.user?.id
  // U4.2: this card holds everyone's open notes. Another user's can be closed
  // with a resolution (the policy allows an admin update) but not reworded.
  const mine = isOwnNote(note.user_id, uid)
  const author = noteAuthor(note.user_id, uid)
  const [editing, setEditing] = useState(false)
  const [body, setBody] = useState(note.body)
  const resRef = useRef<HTMLTextAreaElement>(null)

  const close = async () => {
    if (!res.trim()) { resRef.current?.focus(); toast({ text: 'Say what changed first.' }); return }
    if (await attempt(done.mutateAsync({ id: note.id, done: true, resolution: res }))) onClosed()
  }
  const save = async () => {
    if (!body.trim()) { toast({ text: 'A note needs some words.' }); return }
    if (await attempt(edit.mutateAsync({ id: note.id, body }))) setEditing(false)
  }

  return (
    <div className={`hnote${open ? ' is-open' : ''}`}>
      <button type="button" className="hnote__line" aria-expanded={open} onClick={onToggle}>
        <Tag tone="neutral">{note.kind}</Tag>
        <span className="b">{note.body}</span>
        <span className="meta">{author ? `${author} · ` : ''}{short(new Date(note.created_at))}</span>
      </button>
      {open && (
        <div className="hnote__more">
          {mine && editing && <textarea aria-label="Note" value={body} onChange={e => setBody(e.target.value)} />}
          <textarea ref={resRef} placeholder="Resolution \u2014 what changed" aria-label="Resolution"
            value={res} onChange={e => setRes(e.target.value)} />
          <span className="act">
            <Button size="sm" variant="secondary" busy={done.isPending} onClick={() => void close()}>Close with resolution</Button>
            {!mine ? null : editing
              ? <Button size="sm" variant="quiet" busy={edit.isPending} onClick={() => void save()}>Save edit</Button>
              : <Button size="sm" variant="quiet" onClick={() => setEditing(true)}>Edit</Button>}
          </span>
        </div>
      )}
    </div>
  )
}

/** The field clears when the write lands, not before: a failed note keeps
 *  its words for the retry. */
function AddNote({ add }: { add: ReturnType<typeof useAddNote> }) {
  const [text, setText] = useState('')
  const kind: NoteKind = 'change'
  const submit = async () => {
    if (!text.trim()) { toast({ text: 'Write the note first.' }); return }
    if (await attempt(add.mutateAsync({ body: text, kind }))) setText('')
  }
  return (
    <div className="hadd">
      <textarea aria-label="New note" placeholder="Add a note" value={text} onChange={e => setText(e.target.value)} />
      <Button size="sm" variant="secondary" busy={add.isPending} onClick={() => void submit()}>Add</Button>
    </div>
  )
}
